/**
 * Protestant 66-book canon with per-chapter verse counts (English Standard Version
 * numbering, matching KJV/NIV for chapter and verse bounds). Used by ScriptureRefPicker
 * so references are chosen from real books, chapters and verses — never free-typed.
 */

export type BibleBookId =
  | 'gen'
  | 'exo'
  | 'lev'
  | 'num'
  | 'deu'
  | 'jos'
  | 'jdg'
  | 'rut'
  | '1sa'
  | '2sa'
  | '1ki'
  | '2ki'
  | '1ch'
  | '2ch'
  | 'ezr'
  | 'neh'
  | 'est'
  | 'job'
  | 'psa'
  | 'pro'
  | 'ecc'
  | 'sng'
  | 'isa'
  | 'jer'
  | 'lam'
  | 'ezk'
  | 'dan'
  | 'hos'
  | 'jol'
  | 'amo'
  | 'oba'
  | 'jon'
  | 'mic'
  | 'nam'
  | 'hab'
  | 'zep'
  | 'hag'
  | 'zec'
  | 'mal'
  | 'mat'
  | 'mrk'
  | 'luk'
  | 'jhn'
  | 'act'
  | 'rom'
  | '1co'
  | '2co'
  | 'gal'
  | 'eph'
  | 'php'
  | 'col'
  | '1th'
  | '2th'
  | '1ti'
  | '2ti'
  | 'tit'
  | 'phm'
  | 'heb'
  | 'jas'
  | '1pe'
  | '2pe'
  | '1jn'
  | '2jn'
  | '3jn'
  | 'jud'
  | 'rev';

export type BibleTestament = 'ot' | 'nt';

export interface BibleBook {
  id: BibleBookId;
  name: string;
  testament: BibleTestament;
  /** Verse count per chapter; index 0 is chapter 1. */
  versesPerChapter: readonly number[];
  /** Extra names matched when parsing stored strings. */
  aliases: readonly string[];
}

export interface ScriptureRefParts {
  bookId: BibleBookId;
  chapter: number;
  verse: number;
  /** Inclusive end verse when the reference is a range. */
  verseEnd: number | null;
}

/** Compact chapter→verse counts. Length of the array is the chapter count. */
const BIBLE_BOOKS: readonly BibleBook[] = [
  {
    id: 'gen',
    name: 'Genesis',
    testament: 'ot',
    aliases: ['gen', 'ge', 'gn'],
    versesPerChapter: [
      31, 25, 24, 26, 32, 22, 24, 22, 29, 32, 32, 20, 18, 24, 21, 16, 27, 33, 38, 18, 34, 24, 20, 67,
      34, 35, 46, 22, 35, 43, 55, 32, 20, 31, 29, 43, 36, 30, 23, 23, 57, 38, 34, 34, 28, 34, 31, 22,
      33, 26,
    ],
  },
  {
    id: 'exo',
    name: 'Exodus',
    testament: 'ot',
    aliases: ['exo', 'ex', 'exod'],
    versesPerChapter: [
      22, 25, 22, 31, 23, 30, 25, 32, 35, 29, 10, 51, 22, 31, 27, 36, 16, 27, 25, 26, 36, 31, 33, 18,
      40, 37, 21, 43, 46, 38, 18, 35, 23, 35, 35, 38, 29, 31, 43, 38,
    ],
  },
  {
    id: 'lev',
    name: 'Leviticus',
    testament: 'ot',
    aliases: ['lev', 'le', 'lv'],
    versesPerChapter: [
      17, 16, 17, 35, 19, 30, 38, 36, 24, 20, 47, 8, 59, 57, 33, 34, 16, 30, 37, 27, 24, 33, 44, 23,
      55, 46, 34,
    ],
  },
  {
    id: 'num',
    name: 'Numbers',
    testament: 'ot',
    aliases: ['num', 'nu', 'nm', 'nb'],
    versesPerChapter: [
      54, 34, 51, 49, 31, 27, 89, 26, 36, 35, 16, 33, 45, 41, 50, 13, 32, 22, 29, 35, 41, 30, 25, 18,
      65, 23, 31, 40, 16, 54, 42, 56, 29, 34, 13,
    ],
  },
  {
    id: 'deu',
    name: 'Deuteronomy',
    testament: 'ot',
    aliases: ['deu', 'dt', 'deut'],
    versesPerChapter: [
      46, 37, 29, 49, 33, 25, 26, 20, 29, 22, 32, 32, 18, 29, 23, 22, 20, 22, 21, 20, 23, 30, 25, 22,
      19, 19, 26, 68, 29, 20, 30, 52, 29, 12,
    ],
  },
  {
    id: 'jos',
    name: 'Joshua',
    testament: 'ot',
    aliases: ['jos', 'josh', 'jsh'],
    versesPerChapter: [
      18, 24, 17, 24, 15, 27, 26, 35, 27, 43, 23, 24, 33, 15, 63, 10, 18, 28, 51, 9, 45, 34, 16, 33,
    ],
  },
  {
    id: 'jdg',
    name: 'Judges',
    testament: 'ot',
    aliases: ['jdg', 'judg', 'jg'],
    versesPerChapter: [
      36, 23, 31, 24, 31, 40, 25, 35, 57, 18, 40, 15, 25, 20, 20, 31, 13, 31, 30, 48, 25,
    ],
  },
  {
    id: 'rut',
    name: 'Ruth',
    testament: 'ot',
    aliases: ['rut', 'ru'],
    versesPerChapter: [22, 23, 18, 22],
  },
  {
    id: '1sa',
    name: '1 Samuel',
    testament: 'ot',
    aliases: ['1 samuel', '1 sam', '1sa', '1 sm', 'i samuel', 'i sam'],
    versesPerChapter: [
      28, 36, 21, 22, 12, 21, 17, 22, 27, 27, 15, 25, 23, 52, 35, 23, 58, 30, 24, 42, 15, 23, 29, 22,
      44, 25, 12, 25, 11, 31, 13,
    ],
  },
  {
    id: '2sa',
    name: '2 Samuel',
    testament: 'ot',
    aliases: ['2 samuel', '2 sam', '2sa', '2 sm', 'ii samuel', 'ii sam'],
    versesPerChapter: [
      27, 32, 39, 12, 25, 23, 29, 18, 13, 19, 27, 31, 39, 33, 37, 23, 29, 33, 43, 26, 22, 51, 39, 25,
    ],
  },
  {
    id: '1ki',
    name: '1 Kings',
    testament: 'ot',
    aliases: ['1 kings', '1 ki', '1ki', '1 kgs', 'i kings', 'i ki'],
    versesPerChapter: [
      53, 46, 28, 34, 18, 38, 51, 66, 28, 29, 43, 33, 34, 31, 34, 34, 24, 46, 21, 43, 29, 53,
    ],
  },
  {
    id: '2ki',
    name: '2 Kings',
    testament: 'ot',
    aliases: ['2 kings', '2 ki', '2ki', '2 kgs', 'ii kings', 'ii ki'],
    versesPerChapter: [
      18, 25, 27, 44, 27, 33, 20, 29, 37, 36, 21, 21, 25, 29, 38, 20, 41, 37, 37, 21, 26, 20, 37, 20,
      30,
    ],
  },
  {
    id: '1ch',
    name: '1 Chronicles',
    testament: 'ot',
    aliases: ['1 chronicles', '1 chron', '1 ch', '1ch', 'i chronicles', 'i chron'],
    versesPerChapter: [
      54, 55, 24, 43, 26, 81, 40, 40, 44, 14, 47, 40, 14, 17, 29, 43, 27, 17, 19, 8, 30, 19, 32, 31,
      31, 32, 34, 21, 30,
    ],
  },
  {
    id: '2ch',
    name: '2 Chronicles',
    testament: 'ot',
    aliases: ['2 chronicles', '2 chron', '2 ch', '2ch', 'ii chronicles', 'ii chron'],
    versesPerChapter: [
      17, 18, 17, 22, 14, 42, 22, 18, 31, 19, 23, 16, 22, 15, 19, 14, 19, 34, 11, 37, 20, 12, 21, 27,
      28, 23, 9, 27, 36, 27, 21, 33, 25, 33, 27, 23,
    ],
  },
  {
    id: 'ezr',
    name: 'Ezra',
    testament: 'ot',
    aliases: ['ezr', 'ezra'],
    versesPerChapter: [11, 70, 13, 24, 17, 22, 28, 36, 15, 44],
  },
  {
    id: 'neh',
    name: 'Nehemiah',
    testament: 'ot',
    aliases: ['neh', 'ne'],
    versesPerChapter: [11, 20, 32, 23, 19, 19, 73, 18, 38, 39, 36, 47, 31],
  },
  {
    id: 'est',
    name: 'Esther',
    testament: 'ot',
    aliases: ['est', 'es', 'esth'],
    versesPerChapter: [22, 23, 15, 17, 14, 14, 10, 17, 32, 3],
  },
  {
    id: 'job',
    name: 'Job',
    testament: 'ot',
    aliases: ['job', 'jb'],
    versesPerChapter: [
      22, 13, 26, 21, 27, 30, 21, 22, 35, 22, 20, 25, 28, 22, 35, 22, 16, 21, 29, 29, 34, 30, 17, 25,
      6, 14, 23, 28, 25, 31, 40, 22, 33, 37, 16, 33, 24, 41, 30, 24, 34, 17,
    ],
  },
  {
    id: 'psa',
    name: 'Psalm',
    testament: 'ot',
    aliases: ['psalm', 'psalms', 'ps', 'psa', 'psm'],
    versesPerChapter: [
      6, 12, 8, 8, 12, 10, 17, 9, 20, 18, 7, 8, 6, 7, 5, 11, 15, 50, 14, 9, 13, 31, 6, 10, 22, 12, 14,
      9, 11, 12, 24, 11, 22, 22, 28, 12, 40, 22, 13, 17, 13, 11, 5, 26, 17, 11, 9, 14, 20, 23, 19, 9,
      6, 7, 23, 13, 11, 11, 17, 12, 8, 12, 11, 10, 13, 20, 7, 35, 36, 5, 24, 20, 28, 23, 10, 12, 20,
      72, 13, 19, 16, 8, 18, 12, 13, 17, 7, 18, 52, 17, 16, 15, 5, 23, 11, 13, 12, 9, 9, 5, 8, 28, 22,
      35, 45, 48, 43, 13, 31, 7, 10, 10, 9, 8, 18, 19, 2, 29, 176, 7, 8, 9, 4, 8, 5, 6, 5, 6, 8, 8, 3,
      18, 3, 3, 21, 26, 9, 8, 24, 13, 10, 7, 12, 15, 21, 10, 20, 14, 9, 6,
    ],
  },
  {
    id: 'pro',
    name: 'Proverbs',
    testament: 'ot',
    aliases: ['proverbs', 'prov', 'pro', 'pr'],
    versesPerChapter: [
      33, 22, 35, 27, 23, 35, 27, 36, 18, 32, 31, 28, 25, 35, 33, 33, 28, 24, 29, 30, 31, 29, 35, 34,
      28, 28, 27, 28, 27, 33, 31,
    ],
  },
  {
    id: 'ecc',
    name: 'Ecclesiastes',
    testament: 'ot',
    aliases: ['ecclesiastes', 'eccl', 'ecc', 'ec'],
    versesPerChapter: [18, 26, 22, 16, 20, 12, 29, 17, 18, 20, 10, 14],
  },
  {
    id: 'sng',
    name: 'Song of Solomon',
    testament: 'ot',
    aliases: [
      'song of solomon',
      'song of songs',
      'canticles',
      'sos',
      'song',
      'sg',
      'sng',
      'canticle of canticles',
    ],
    versesPerChapter: [17, 17, 11, 16, 16, 13, 13, 14],
  },
  {
    id: 'isa',
    name: 'Isaiah',
    testament: 'ot',
    aliases: ['isaiah', 'isa', 'is'],
    versesPerChapter: [
      31, 22, 26, 6, 30, 13, 25, 22, 21, 34, 16, 6, 22, 32, 9, 14, 14, 7, 25, 6, 17, 25, 18, 23, 12,
      21, 13, 29, 24, 33, 9, 20, 24, 17, 10, 22, 38, 22, 8, 31, 29, 25, 28, 28, 25, 13, 15, 22, 26, 11,
      23, 15, 12, 17, 13, 12, 21, 14, 21, 22, 11, 12, 19, 12, 25, 24,
    ],
  },
  {
    id: 'jer',
    name: 'Jeremiah',
    testament: 'ot',
    aliases: ['jeremiah', 'jer', 'je'],
    versesPerChapter: [
      19, 37, 25, 31, 31, 30, 34, 22, 26, 25, 23, 17, 27, 22, 21, 21, 27, 23, 15, 18, 14, 30, 40, 10,
      38, 24, 22, 17, 32, 24, 40, 44, 26, 22, 19, 32, 21, 28, 18, 16, 18, 22, 13, 30, 5, 28, 7, 47, 39,
      46, 64, 34,
    ],
  },
  {
    id: 'lam',
    name: 'Lamentations',
    testament: 'ot',
    aliases: ['lamentations', 'lam', 'la'],
    versesPerChapter: [22, 22, 66, 22, 22],
  },
  {
    id: 'ezk',
    name: 'Ezekiel',
    testament: 'ot',
    aliases: ['ezekiel', 'ezek', 'eze', 'ezk'],
    versesPerChapter: [
      28, 10, 27, 17, 17, 14, 27, 18, 11, 22, 25, 28, 23, 23, 8, 63, 24, 32, 14, 49, 32, 31, 49, 27,
      17, 21, 36, 26, 21, 26, 18, 32, 33, 31, 15, 38, 28, 23, 29, 49, 26, 20, 27, 31, 25, 24, 23, 35,
    ],
  },
  {
    id: 'dan',
    name: 'Daniel',
    testament: 'ot',
    aliases: ['daniel', 'dan', 'da', 'dn'],
    versesPerChapter: [21, 49, 30, 37, 31, 28, 28, 27, 27, 21, 45, 13],
  },
  {
    id: 'hos',
    name: 'Hosea',
    testament: 'ot',
    aliases: ['hosea', 'hos', 'ho'],
    versesPerChapter: [11, 23, 5, 19, 15, 11, 16, 14, 17, 15, 12, 14, 16, 9],
  },
  {
    id: 'jol',
    name: 'Joel',
    testament: 'ot',
    aliases: ['joel', 'jol', 'jl'],
    versesPerChapter: [20, 32, 21],
  },
  {
    id: 'amo',
    name: 'Amos',
    testament: 'ot',
    aliases: ['amos', 'amo', 'am'],
    versesPerChapter: [15, 16, 15, 13, 27, 14, 17, 14, 15],
  },
  {
    id: 'oba',
    name: 'Obadiah',
    testament: 'ot',
    aliases: ['obadiah', 'oba', 'ob'],
    versesPerChapter: [21],
  },
  {
    id: 'jon',
    name: 'Jonah',
    testament: 'ot',
    aliases: ['jonah', 'jon', 'jnh'],
    versesPerChapter: [17, 10, 10, 11],
  },
  {
    id: 'mic',
    name: 'Micah',
    testament: 'ot',
    aliases: ['micah', 'mic', 'mc'],
    versesPerChapter: [16, 13, 12, 13, 15, 16, 20],
  },
  {
    id: 'nam',
    name: 'Nahum',
    testament: 'ot',
    aliases: ['nahum', 'nah', 'na', 'nam'],
    versesPerChapter: [15, 13, 19],
  },
  {
    id: 'hab',
    name: 'Habakkuk',
    testament: 'ot',
    aliases: ['habakkuk', 'hab', 'hb'],
    versesPerChapter: [17, 20, 19],
  },
  {
    id: 'zep',
    name: 'Zephaniah',
    testament: 'ot',
    aliases: ['zephaniah', 'zeph', 'zep', 'zp'],
    versesPerChapter: [18, 15, 20],
  },
  {
    id: 'hag',
    name: 'Haggai',
    testament: 'ot',
    aliases: ['haggai', 'hag', 'hg'],
    versesPerChapter: [15, 23],
  },
  {
    id: 'zec',
    name: 'Zechariah',
    testament: 'ot',
    aliases: ['zechariah', 'zech', 'zec', 'zc'],
    versesPerChapter: [21, 13, 10, 14, 11, 15, 14, 23, 17, 12, 17, 14, 9, 21],
  },
  {
    id: 'mal',
    name: 'Malachi',
    testament: 'ot',
    aliases: ['malachi', 'mal', 'ml'],
    versesPerChapter: [14, 17, 18, 6],
  },
  {
    id: 'mat',
    name: 'Matthew',
    testament: 'nt',
    aliases: ['matthew', 'matt', 'mat', 'mt'],
    versesPerChapter: [
      25, 23, 17, 25, 48, 34, 29, 34, 38, 42, 30, 50, 58, 36, 39, 28, 27, 35, 30, 34, 46, 46, 39, 51,
      46, 75, 66, 20,
    ],
  },
  {
    id: 'mrk',
    name: 'Mark',
    testament: 'nt',
    aliases: ['mark', 'mrk', 'mk', 'mr'],
    versesPerChapter: [45, 28, 35, 41, 43, 56, 37, 38, 62, 52, 33, 44, 37, 72, 47, 20],
  },
  {
    id: 'luk',
    name: 'Luke',
    testament: 'nt',
    aliases: ['luke', 'luk', 'lk'],
    versesPerChapter: [
      80, 52, 38, 44, 39, 49, 50, 56, 62, 42, 54, 59, 35, 35, 32, 31, 37, 43, 48, 47, 38, 71, 56, 53,
    ],
  },
  {
    id: 'jhn',
    name: 'John',
    testament: 'nt',
    aliases: ['john', 'jhn', 'jn', 'joh'],
    versesPerChapter: [
      51, 25, 36, 54, 47, 71, 53, 59, 41, 42, 57, 50, 38, 31, 27, 33, 26, 40, 42, 31, 25,
    ],
  },
  {
    id: 'act',
    name: 'Acts',
    testament: 'nt',
    aliases: ['acts', 'act', 'ac'],
    versesPerChapter: [
      26, 47, 26, 37, 42, 15, 60, 40, 43, 48, 30, 25, 52, 28, 41, 40, 34, 28, 41, 38, 40, 30, 35, 27,
      27, 32, 44, 31,
    ],
  },
  {
    id: 'rom',
    name: 'Romans',
    testament: 'nt',
    aliases: ['romans', 'rom', 'ro', 'rm'],
    versesPerChapter: [32, 29, 31, 25, 21, 23, 25, 39, 33, 21, 36, 21, 14, 23, 33, 27],
  },
  {
    id: '1co',
    name: '1 Corinthians',
    testament: 'nt',
    aliases: ['1 corinthians', '1 cor', '1co', 'i corinthians', 'i cor'],
    versesPerChapter: [31, 16, 23, 21, 13, 20, 40, 13, 27, 33, 34, 31, 13, 40, 58, 24],
  },
  {
    id: '2co',
    name: '2 Corinthians',
    testament: 'nt',
    aliases: ['2 corinthians', '2 cor', '2co', 'ii corinthians', 'ii cor'],
    versesPerChapter: [24, 17, 18, 18, 21, 18, 16, 24, 15, 18, 33, 21, 14],
  },
  {
    id: 'gal',
    name: 'Galatians',
    testament: 'nt',
    aliases: ['galatians', 'gal', 'ga'],
    versesPerChapter: [24, 21, 29, 31, 26, 18],
  },
  {
    id: 'eph',
    name: 'Ephesians',
    testament: 'nt',
    aliases: ['ephesians', 'eph', 'ep'],
    versesPerChapter: [23, 22, 21, 32, 33, 24],
  },
  {
    id: 'php',
    name: 'Philippians',
    testament: 'nt',
    aliases: ['philippians', 'phil', 'php', 'pp'],
    versesPerChapter: [30, 30, 21, 23],
  },
  {
    id: 'col',
    name: 'Colossians',
    testament: 'nt',
    aliases: ['colossians', 'col'],
    versesPerChapter: [29, 23, 25, 18],
  },
  {
    id: '1th',
    name: '1 Thessalonians',
    testament: 'nt',
    aliases: ['1 thessalonians', '1 thess', '1 th', '1th', 'i thessalonians', 'i thess'],
    versesPerChapter: [10, 20, 13, 18, 28],
  },
  {
    id: '2th',
    name: '2 Thessalonians',
    testament: 'nt',
    aliases: ['2 thessalonians', '2 thess', '2 th', '2th', 'ii thessalonians', 'ii thess'],
    versesPerChapter: [12, 17, 18],
  },
  {
    id: '1ti',
    name: '1 Timothy',
    testament: 'nt',
    aliases: ['1 timothy', '1 tim', '1 ti', '1ti', 'i timothy', 'i tim'],
    versesPerChapter: [20, 15, 16, 16, 25, 21],
  },
  {
    id: '2ti',
    name: '2 Timothy',
    testament: 'nt',
    aliases: ['2 timothy', '2 tim', '2 ti', '2ti', 'ii timothy', 'ii tim'],
    versesPerChapter: [18, 26, 17, 22],
  },
  {
    id: 'tit',
    name: 'Titus',
    testament: 'nt',
    aliases: ['titus', 'tit', 'ti'],
    versesPerChapter: [16, 15, 15],
  },
  {
    id: 'phm',
    name: 'Philemon',
    testament: 'nt',
    aliases: ['philemon', 'phm', 'pm'],
    versesPerChapter: [25],
  },
  {
    id: 'heb',
    name: 'Hebrews',
    testament: 'nt',
    aliases: ['hebrews', 'heb'],
    versesPerChapter: [14, 18, 19, 16, 14, 20, 28, 13, 28, 39, 40, 29, 25],
  },
  {
    id: 'jas',
    name: 'James',
    testament: 'nt',
    aliases: ['james', 'jas', 'jm'],
    versesPerChapter: [27, 26, 18, 17, 20],
  },
  {
    id: '1pe',
    name: '1 Peter',
    testament: 'nt',
    aliases: ['1 peter', '1 pet', '1 pe', '1pe', 'i peter', 'i pet'],
    versesPerChapter: [25, 25, 22, 19, 14],
  },
  {
    id: '2pe',
    name: '2 Peter',
    testament: 'nt',
    aliases: ['2 peter', '2 pet', '2 pe', '2pe', 'ii peter', 'ii pet'],
    versesPerChapter: [21, 22, 18],
  },
  {
    id: '1jn',
    name: '1 John',
    testament: 'nt',
    aliases: ['1 john', '1 jn', '1jn', '1 jo', 'i john', 'i jn'],
    versesPerChapter: [10, 29, 24, 21, 21],
  },
  {
    id: '2jn',
    name: '2 John',
    testament: 'nt',
    aliases: ['2 john', '2 jn', '2jn', '2 jo', 'ii john', 'ii jn'],
    versesPerChapter: [13],
  },
  {
    id: '3jn',
    name: '3 John',
    testament: 'nt',
    aliases: ['3 john', '3 jn', '3jn', '3 jo', 'iii john', 'iii jn'],
    versesPerChapter: [14],
  },
  {
    id: 'jud',
    name: 'Jude',
    testament: 'nt',
    aliases: ['jude', 'jud', 'jd'],
    versesPerChapter: [25],
  },
  {
    id: 'rev',
    name: 'Revelation',
    testament: 'nt',
    aliases: ['revelation', 'rev', 're', 'apocalypse', 'apoc'],
    versesPerChapter: [
      20, 29, 22, 11, 14, 17, 17, 13, 21, 11, 19, 17, 18, 20, 8, 21, 18, 24, 21, 15, 27, 21,
    ],
  },
] as const;

const bookById = new Map<BibleBookId, BibleBook>(BIBLE_BOOKS.map((book) => [book.id, book]));

/** Longest alias first so "1 John" wins over "John". */
const bookMatchers: { book: BibleBook; alias: string }[] = BIBLE_BOOKS.flatMap((book) =>
  [book.name, ...book.aliases].map((alias) => ({
    book,
    alias: alias.toLowerCase(),
  })),
).sort((a, b) => b.alias.length - a.alias.length);

export const bibleBooks = BIBLE_BOOKS;

export const getBibleBook = (id: BibleBookId): BibleBook => {
  const book = bookById.get(id);
  if (!book) throw new Error(`Unknown bible book: ${id}`);
  return book;
};

export const chapterCount = (bookId: BibleBookId): number =>
  getBibleBook(bookId).versesPerChapter.length;

export const verseCount = (bookId: BibleBookId, chapter: number): number => {
  const book = getBibleBook(bookId);
  if (chapter < 1 || chapter > book.versesPerChapter.length) return 0;
  return book.versesPerChapter[chapter - 1] ?? 0;
};

export const formatScriptureRef = (parts: ScriptureRefParts): string => {
  const book = getBibleBook(parts.bookId);
  const base = `${book.name} ${parts.chapter}:${parts.verse}`;
  if (parts.verseEnd != null && parts.verseEnd > parts.verse) {
    return `${base}–${parts.verseEnd}`;
  }
  return base;
};

const REF_PATTERN =
  /^(.+?)\s+(\d+)\s*:\s*(\d+)(?:\s*[–\-—]\s*(\d+))?\s*$/u;

export const parseScriptureRef = (input: string): ScriptureRefParts | null => {
  const trimmed = input.trim();
  if (!trimmed) return null;

  const match = REF_PATTERN.exec(trimmed);
  if (!match) return null;

  const bookToken = match[1]!.trim().toLowerCase().replace(/\s+/g, ' ');
  const chapter = Number(match[2]);
  const verse = Number(match[3]);
  const verseEndRaw = match[4] ? Number(match[4]) : null;

  const found = bookMatchers.find(({ alias }) => alias === bookToken);
  if (!found) return null;

  const { book } = found;
  if (chapter < 1 || chapter > book.versesPerChapter.length) return null;
  const maxVerse = book.versesPerChapter[chapter - 1] ?? 0;
  if (verse < 1 || verse > maxVerse) return null;

  let verseEnd: number | null = null;
  if (verseEndRaw != null) {
    if (verseEndRaw <= verse || verseEndRaw > maxVerse) return null;
    verseEnd = verseEndRaw;
  }

  return {
    bookId: book.id,
    chapter,
    verse,
    verseEnd,
  };
};

export const isCompleteScriptureRef = (
  parts: Partial<ScriptureRefParts> & {
    bookId: BibleBookId | null;
    chapter: number | null;
    verse: number | null;
  },
): parts is ScriptureRefParts & { bookId: BibleBookId; chapter: number; verse: number } =>
  parts.bookId != null && parts.chapter != null && parts.verse != null;
